import type {
  BankDispensary,
  BankDispensaryMetadataData,
  CustomRequirementResult,
  DocumentUpload,
  DueDiligenceDocument,
  MinifiedAccountUser,
  MinifiedCrbTemplateInfo,
  MinifiedDispensary,
  MUO,
  MUOBankResponse,
  OnboardingDocumentRequirement,
  OnboardingDocumentRequirementResult,
  TemplateResponse,
  TemplateResultResponse,
  User,
  VerboseCrbQuestionnaireUsageResponse
} from '@gcv/shared';
import { makeAutoObservable } from 'mobx';

import { DispensariesApi } from 'api';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { getUserStore } from 'stores/UserStore';
import type { FilterListChild } from 'ui';

export interface IdentificationDocument {
  base64: string;
  type: string;
  blob?: Blob;
}

export interface KeyAccountData {
  icon?: 'party-blooper' | 'tick';
  date: string;
  formattedDate: string;
  description: string;
}

export interface InvitedDispensary extends MinifiedDispensary {
  invited: boolean;
  invited_by_org_id: string;
  invited_id: string;
}

export class Model {
  constructor() {
    makeAutoObservable(this);
  }

  accountsSearchTerm = '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  accountsFilterRef: React.MutableRefObject<any> | undefined = undefined;

  filterByAccountsSearchTerm = (
    dispensaries: MinifiedDispensary[]
  ): (MinifiedDispensary | InvitedDispensary)[] => {
    if (this.accountsSearchTerm.length === 0) {
      return dispensaries;
    }

    const term = this.accountsSearchTerm.toLowerCase();

    return dispensaries.filter((d) => {
      const dispensary = d.name?.toLowerCase();

      let internalIds: string[] = [];

      if (d.internal_metadata && d.internal_metadata.accounts) {
        internalIds = [...internalIds, ...Object.keys(d.internal_metadata.accounts)];
      }

      const hasDispensaryOfInternalIds = internalIds?.some(
        (internalId) => internalId.toLowerCase().indexOf(term.toLowerCase()) != -1
      );
      return dispensary.indexOf(term) != -1 || hasDispensaryOfInternalIds;
    });
  };

  filterInvitedByAccountsSearchTerm = (dispensaries: InvitedDispensary[]): InvitedDispensary[] => {
    if (this.accountsSearchTerm.length === 0) {
      return dispensaries;
    }

    const term = this.accountsSearchTerm.toLowerCase();

    return dispensaries.filter((d) => {
      const dispensary = d.name?.toLowerCase();

      let internalIds: string[] = [];

      if (d.internal_metadata && d.internal_metadata.accounts) {
        internalIds = [...internalIds, ...Object.keys(d.internal_metadata.accounts)];
      }

      const hasDispensaryOfInternalIds = internalIds?.some(
        (internalId) => internalId.toLowerCase().indexOf(term.toLowerCase()) != -1
      );
      return dispensary.indexOf(term) != -1 || hasDispensaryOfInternalIds;
    });
  };

  dispensariesApi = new DispensariesApi();
  userStore = getUserStore();
  snackbarStore = getSnackbarStore();
  searchTerms: FilterListChild[] = [] as FilterListChild[];

  activeDispensaries: MinifiedDispensary[] = [] as MinifiedDispensary[];
  reviewDispensaries: MinifiedDispensary[] = [] as MinifiedDispensary[];
  waitingDispensaries: MinifiedDispensary[] = [] as MinifiedDispensary[];
  invitedDispensaries: InvitedDispensary[] = [] as InvitedDispensary[];
  filteredBankMuos: MUOBankResponse[] = [] as MUOBankResponse[];
  // Specific to a single dispensary for view, review, and requirement pages
  currentOrg: BankDispensary | MUO = {} as BankDispensary;
  currentOrgMetadata?: BankDispensaryMetadataData = {} as BankDispensaryMetadataData;
  templateInfo: MinifiedCrbTemplateInfo = undefined as unknown as MinifiedCrbTemplateInfo;
  dispensaryStaff: User[] = [] as User[];
  accountOwners: User[] = [] as User[];
  accountUsers: MinifiedAccountUser[] = [] as MinifiedAccountUser[];
  /**
   * All questionnaires for the CRB to populate the Open Questionnaires table on the account review page
   */
  questionnairesForAccountReview: VerboseCrbQuestionnaireUsageResponse[] = [];

  // new template
  onboardingTemplate: TemplateResponse = {} as TemplateResponse;
  onboardingTemplateResult: TemplateResultResponse = {} as TemplateResultResponse;
  onboardingDocumentResultMap: { [requirement_id: string]: OnboardingDocumentRequirementResult } = {};

  // Additional Info
  initialCustomRequirementResults: CustomRequirementResult[] = [];
  isSavingAdditionalInfo = false;
  isCompletingAdditionalInfo = false;
  additionalInfoLastUpdated = '';

  prevTemplateName = '';
  onboardingChildTemplate: TemplateResponse = {} as TemplateResponse;
  newDueDiligenceTemplate: TemplateResponse | Record<string, never> = {} as TemplateResponse;

  loadingDispensary = true;
  savingOperationalDetails = false;
  savingBusinessDetails = false;

  // Questionnaires
  forceReloadQuestionnaires = false;

  // Account Owners
  selectedUser?: User = undefined;
  loadingIdDocuments = false;
  idFrontDocument?: IdentificationDocument | undefined = undefined;
  idBackDocument?: IdentificationDocument | undefined = undefined;
  accountUserDrawerOpen = false;
  resendWelecomeEmailLoading = false;
  resendEmailSent = false;
  userEditModalOpen = false;
  editUserLoading = false;
  idFrontInfo?: DocumentUpload = undefined;
  idBackInfo?: DocumentUpload = undefined;
  loadingAccountUsers = false;

  isPayqwickEntityModalOpen = false;
  isApprovingPayqwickUserLoading = false;
  /**
   * When approving a user for Payqwick, if there are multiple entity IDs tied to this CRB, the FI needs to select one for this user.
   * This holds the user ID pending entity selection
   */
  currentPayqwickApprovalUserId?: string = undefined;
  /**
   * When approving a user for Payqwick, if there are multiple entity IDs tied to this CRB, the FI needs to select one for this user.
   * This holds the entity IDs used in the dropdown
   */
  currentPayqwickApprovalEntityIds: string[] = [];

  currentGcdApprovalUser?: User = undefined;
  isGcdApprovalModalOpen = false;
  isApprovingGcdUserLoading = false;

  inviteUserModalOpen = false;
  invitingUser = false;

  //documents
  loadingDocuments = false;
  uploadingFiles = false;
  documents = [] as DocumentUpload[];
  allDocumentsMap: { [id: string]: DocumentUpload } = {};

  renaming = false;
  creatingLocation = false;
  associatingParent = false;

  //permissions
  hasLoadedPermissions = false;
  canUserApproveAccounts = false;
  canUserApproveRequirements = false;
  canUserApproveDocuments = false;
  canUserUploadCommentAndRequest = false;
  canUserChangeStatus = false;
  canUserInviteUsers = false;
  isBankExaminer = false;
  userCanEditOrganizationalDetails = false;

  dispensaryAllRootDocuments: {
    requirement: OnboardingDocumentRequirement;
    ddDocuments: DueDiligenceDocument[];
    documents: DocumentUpload[];
  }[] = [];

  openDrawer = false;
  docId = '';

  // key account dates
  keyAccountData: KeyAccountData[] = [];

  awaitingReviewCount = 0;
  isSorting = false;
}

export const model = new Model();
